import type {VendorDirectoryData, VendorRecord, VendorRow, VendorWareRecord, WareAggregateRow, WareCategory, WareVendorRow} from "./types";
import {UserLocation} from "../../types/swc.types.ts";
import {ParsedLocation} from "../../types/general.types.ts";
import {v4} from "uuid";

const CATEGORY_ORDER: WareCategory[] = ['items', 'droids', 'creatures', 'materials', 'datacards', 'unknown'];

interface AggregateAccumulator {
    id: string;
    name: string;
    normalizedName: string;
    wareKey: string;
    category: WareCategory;
    rawTypes: Set<string>;
    subTypes: Set<string>;
    vendorIds: Set<string>;
    listingCount: number;
    totalCount: number;
    priceTotal: number;
    priceCount: number;
    minPrice: number | null;
    maxPrice: number | null;
    currencies: Set<string>;
    matchedListings: number;
    unmatchedListings: number;
}

const createCategoryCounts = (): Record<WareCategory, number> => ({
    items: 0,
    droids: 0,
    creatures: 0,
    materials: 0,
    datacards: 0,
    unknown: 0,
});

const normalizeSpace = (value: string): string => value.trim().replace(/\s+/g, ' ');

export const normalizeWareName = (value: string): string => normalizeSpace(value).toLowerCase();
const getWareDisplayName = (ware: VendorWareRecord): string => normalizeSpace(ware.entityName || ware.name);

export const toWareKey = (value: string): string => {
    const slug = normalizeSpace(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return slug || 'ware';
};

export const normalizeWareCategory = (value: string): WareCategory => {
    const raw = value.trim().toLowerCase();

    if (raw === 'item' || raw === 'items') return 'items';
    if (raw === 'droid' || raw === 'droids') return 'droids';
    if (raw === 'creature' || raw === 'creatures') return 'creatures';
    if (raw === 'material' || raw === 'materials') return 'materials';
    if (raw === 'datacard' || raw === 'datacards') return 'datacards';

    return 'unknown';
};

export const getWareCategoryLabel = (category: WareCategory): string => {
    if (category === 'unknown') return 'Unknown';
    return category.charAt(0).toUpperCase() + category.slice(1);
};

export const getWareCategoryColor = (category: WareCategory): string => {
    switch (category) {
        case 'items':
            return 'blue';
        case 'droids':
            return 'cyan';
        case 'creatures':
            return 'green';
        case 'materials':
            return 'orange';
        case 'datacards':
            return 'yellow'
        default:
            return 'gray';
    }
};

export const getVendorId = (vendor: VendorRecord): string => vendor.id ?? vendor._id ?? vendor.name;

export const isWareMatched = (ware: VendorWareRecord): boolean => {
    return typeof ware.entityId === 'string' && ware.entityId.trim() !== '';
};

export const buildVendorDirectoryData = (vendors: VendorRecord[]): VendorDirectoryData => {
    const aggregateMap = new Map<string, AggregateAccumulator>();

    const vendorRows: VendorRow[] = vendors.map((vendor) => {
        const typeCounts = createCategoryCounts();

        for (const ware of vendor.wares ?? []) {
            const category = normalizeWareCategory(ware.type);
            typeCounts[category] += 1;

            const matched = isWareMatched(ware);

            const displayName = getWareDisplayName(ware);
            const normalizedName = normalizeWareName(displayName);
            const aggregateKey = `${category}:${normalizedName}`;
            const vendorId = getVendorId(vendor);
            const current = aggregateMap.get(aggregateKey) ?? {
                id: aggregateKey,
                name: displayName,
                normalizedName,
                wareKey: toWareKey(displayName),
                category,
                rawTypes: new Set<string>(),
                subTypes: new Set<string>(),
                vendorIds: new Set<string>(),
                listingCount: 0,
                totalCount: 0,
                priceTotal: 0,
                priceCount: 0,
                minPrice: null,
                maxPrice: null,
                currencies: new Set<string>(),
                matchedListings: 0,
                unmatchedListings: 0,
            } satisfies AggregateAccumulator;

            current.rawTypes.add(ware.type);
            if (typeof ware.subType === 'string' && ware.subType.trim() !== '') {
                current.subTypes.add(normalizeSpace(ware.subType));
            }
            current.vendorIds.add(vendorId);
            current.listingCount += 1;
            current.totalCount += ware.quantity;
            current.currencies.add(ware.currency);
            current.priceTotal += ware.price;
            current.priceCount += 1;
            current.minPrice = current.minPrice === null ? ware.price : Math.min(current.minPrice, ware.price);
            current.maxPrice = current.maxPrice === null ? ware.price : Math.max(current.maxPrice, ware.price);
            if (matched) {
                current.matchedListings += 1;
            } else {
                current.unmatchedListings += 1;
            }

            aggregateMap.set(aggregateKey, current);
        }

        return {
            ...vendor,
            waresCount: (vendor.wares ?? []).length,
            typeCounts,
        };
    });

    const wareRows = Array.from(aggregateMap.values())
        .map<WareAggregateRow>((row) => ({
            id: row.id,
            name: row.name,
            wareKey: row.wareKey,
            normalizedName: row.normalizedName,
            rawTypes: Array.from(row.rawTypes).sort((a, b) => a.localeCompare(b)),
            subTypes: Array.from(row.subTypes).sort((a, b) => a.localeCompare(b)),
            category: row.category,
            vendorCount: row.vendorIds.size,
            listingCount: row.listingCount,
            totalCount: row.totalCount,
            minPrice: row.minPrice,
            maxPrice: row.maxPrice,
            avgPrice: row.priceCount > 0 ? row.priceTotal / row.priceCount : null,
            currencies: Array.from(row.currencies).sort((a, b) => a.localeCompare(b)),
            matchedListings: row.matchedListings,
            unmatchedListings: row.unmatchedListings,
        }))
        .sort((a, b) => {
            const byCategory = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
            if (byCategory !== 0) return byCategory;
            return a.name.localeCompare(b.name);
        });

    const wareRowsByCategory = CATEGORY_ORDER.reduce<Record<WareCategory, WareAggregateRow[]>>((acc, category) => {
        acc[category] = wareRows.filter(row => row.category === category);
        return acc;
    }, {
        items: [],
        droids: [],
        creatures: [],
        materials: [],
        datacards: [],
        unknown: [],
    });

    const stats = {
        vendorCount: vendorRows.length,
        totalListings: vendors.reduce((acc, vendor) => acc + (vendor.wares?.length ?? 0), 0),
        distinctWares: wareRows.length,
        categoryCounts: wareRows.reduce<Record<WareCategory, number>>((acc, row) => {
            acc[row.category] += 1;
            return acc;
        }, createCategoryCounts()),
    };

    return {
        vendors,
        vendorRows,
        wareRows,
        wareRowsByCategory,
        stats,
    };
};

const calculateDistance = (from: UserLocation, to: ParsedLocation) => {
    if(!from) {
        return null;
    }
    return Math.max(
      Math.abs(from.coordinates.galaxy.attributes.x - to.coordinates.galaxy.x),
      Math.abs(from.coordinates.galaxy.attributes.y - to.coordinates.galaxy.y),
    )
}

export const getWareVendorRows = (
    vendors: VendorRecord[],
    target: Pick<WareAggregateRow, 'category' | 'normalizedName'>,
    location: UserLocation | null
): WareVendorRow[] => {
    const rows: WareVendorRow[] = [];

    for (const vendor of vendors) {
        const vendorId = getVendorId(vendor);
        for (const ware of vendor.wares ?? []) {
            const category = normalizeWareCategory(ware.type);
            if (category !== target.category) {
                continue;
            }
            if (normalizeWareName(getWareDisplayName(ware)) !== target.normalizedName) {
                continue;
            }

            rows.push({
                ...ware,
                id: v4(),
                vendorId,
                vendorName: vendor.name,
                ownerName: vendor.ownerName ?? 'Unknown',
                location: vendor.location,
                distance: location ? calculateDistance(location, vendor.location) : null,
                category,
            });
        }
    }

    return rows;
};

export const getCategoryOrder = (): WareCategory[] => CATEGORY_ORDER;
